import _ from 'lodash'
import * as $rdf from 'rdflib'
import moment from 'moment'
import Graph from '@/rdf/Graph'
import {
  DASH, DCT, DCAT, FDPO, METACAT, RDF, RDFS, XSD, EFO, FOAF, VCARD,
  DCATAP,
  HDCATAP,
  VOID,
} from '@/rdf/namespaces'
import rdfUtils from '@/rdf/utils'
import config from '@/config'
import fieldUtils from '@/components/ShaclForm/fieldUtils'
import valueUtils from '@/components/ShaclForm/valueUtils'

function field(label, input, extra = {}) {
  if (typeof input !== 'object') {
    return {
      label,
      value: input,
      ...extra,
    }
  }

  if (Array.isArray(input)) {
    return {
      label,
      items: input,
      ...extra,
    }
  }

  return {
    label,
    value: input.label,
    uri: input.uri,
    ...extra,
  }
}

function dateField(label, input, extra = {}) {
  return field(label, moment(input).format(config.dateFormat), extra)
}

function itemFromPath(path) {
  if (!path) return null

  return {
    label: rdfUtils.pathTerm(path),
    uri: path,
  }
}

function commonMetadata(graph: Graph) {
  const metadataGroups = []
  // const actualEndpointUrl = config.sparqlEndpointURL
  const endpointUrl = graph.findAll(DCAT('endpointURL'))
  const actualEndpointUrl = endpointUrl.find((uri) => uri.endsWith('sparql')) ?? undefined
  if (actualEndpointUrl) {
    const data = {
      label: actualEndpointUrl,
      resolved: true,
    }
    metadataGroups.push({ fields: [field('SPARQL Endpoint', data)] })
  }

  const conformsTo = graph.findAll(DCT('conformsTo'))
  if (conformsTo.length > 0) {
    const data = conformsTo.map((uri) => {
      const label = graph.findOne(RDFS('label'), {
        subject: $rdf.namedNode(`${uri}`),
      })

      return {
        label: label || rdfUtils.pathTerm(`${uri}`),
        uri: uri.replace(config.persistentURL(), config.clientURL),
        resolved: true,
      }
    })
    metadataGroups.push({ fields: [field('Conforms to', data)] })
  }
  const acronym = graph.findOne(METACAT('hasAcronym'))
  if (acronym) {
    metadataGroups.push({ fields: [field('Acronym', acronym)] })
  }

  const pages = graph.findAll(FOAF('page'))
  if (pages.length > 0) {
    const data = pages.map((uri) => {
      const label = graph.findOne(RDFS('label'), {
        subject: $rdf.namedNode(`${uri}`),
      })
      return {
        label: label || uri,
        uri,
        resolved: false,
      }
    })
    metadataGroups.push({ fields: [field('Interactive Exploration', data)] })
  }

  const abstract = graph.findOne(DCT('abstract'))
  if (abstract) {
    const data = abstract
    metadataGroups.push({ fields: [field('Abstract', data)] })
  }

  const temporalCoverage = graph.findAll(DCT('temporal'))
  if (temporalCoverage.length > 0) {
    const data = temporalCoverage.map((uri) => {
      const start = graph.findOne(DCAT('startDate'), {
        subject: $rdf.blankNode(`${uri}`),
      })
      const end = graph.findOne(DCAT('endDate'), {
        subject: $rdf.blankNode(`${uri}`),
      })
      let temporalCoverageString = ''
      if (start && end) {
        temporalCoverageString = `${start} - ${end}`
      }
      const comment = graph.findOne(RDFS('comment'), {
        subject: $rdf.blankNode(`${uri}`),
      })
      if (comment) {
        temporalCoverageString += ` (${comment})`
      }

      return {
        label: temporalCoverageString,
        uri: null,
        resolved: true,
      }
    })
    metadataGroups.push({ fields: [field('Temporal Coverage', data)] })
  }

  const principalInvestigator = graph.findAll(METACAT('hasPrincipalInvestigator'))
  if (principalInvestigator.length > 0) {
    const data = principalInvestigator.map((uri) => ({
      label: uri,
      uri: null,
      resolved: true,
    }))
    metadataGroups.push({ fields: [field('Principal Investigator', data)] })
  }

  const contactPoint = graph.findAll(DCAT('contactPoint'))
  if (contactPoint.length > 0) {
    const data = contactPoint.map((uri) => {
      const name = graph.findOne(VCARD('fn'), {
        subject: $rdf.namedNode(`${uri}`),
      })
      const email = graph.findOne(VCARD('hasEmail'), {
        subject: $rdf.namedNode(`${uri}`),
      })
      let label = ''
      if (name !== undefined) {
        label = `${name}`
      }
      if (email !== undefined) {
        if (name !== undefined) {
          label = `${label} (${email})`
        } else {
          label = `${email}`
        }
      }
      return {
        label,
        uri: null,
        resolved: true,
      }
    })
    metadataGroups.push({ fields: [field('Contact Point', data)] })
  }

  const source = graph.findAll(DCT('source'))
  if (source.length > 0) {
    const data = source.map((uri) => {
      let label = uri
      if (uri.startsWith('http')) {
        label = graph.findOne(RDFS('label'), {
          subject: $rdf.namedNode(`${uri}`),
        })
      }
      return {
        label,
        uri: uri.startsWith('http') ? uri : null,
        resolved: true,
      }
    })
    metadataGroups.push({ fields: [field('Source(s)', data)] })
  }

  const themeTaxonomy = graph.findAll(DCAT('themeTaxonomy'))
  if (themeTaxonomy.length > 0) {
    const data = themeTaxonomy.map((uri) => {
      let label = uri
      if (uri.startsWith('http')) {
        label = graph.findOne(RDFS('label'), {
          subject: $rdf.namedNode(`${uri}`),
        })
      }
      return {
        label,
        uri: uri.startsWith('http') ? uri : null,
        resolved: true,
      }
    })
    metadataGroups.push({ fields: [field('Theme Taxonomy', data)] })
  }

  const publication = graph.findAll(METACAT('hasPublication'))
  if (publication.length > 0) {
    const data = publication.map((uri) => ({
      label: uri,
      uri,
      resolved: true,
    }))
    metadataGroups.push({ fields: [field('Publication', data)] })
  }

  const multivaluedProperties = new Set<string>(['hasAdditionalData', 'hasDataType', 'hasConsentType', 'hasInclusionCriteria', 'hasExclusionCriteria', 'hasDataUseRestriction', 'applicableLegislation', 'healthCategory', 'hasCodingSystem'])
  const literalProperties = new Set<string>(['hasAgeRange'])

  const orgPropertiesMap = new Map<string, string>()
  orgPropertiesMap.set('hasDataProvider', 'Data Provider')
  orgPropertiesMap.set('hasFundingOrganization', 'Funding Organization')
  orgPropertiesMap.set('hasProjectPartner', 'Project Partner')

  Array.from(orgPropertiesMap.keys()).forEach((key) => {
    const value = orgPropertiesMap.get(key)
    const propertyValues = graph.findAll(METACAT(key))
    if (propertyValues.length > 0) {
      const data = propertyValues.map((propValue) => {
        let valueLabel = propValue
        const label = graph.findOne(RDFS('label'), {
          subject: $rdf.blankNode(`${propValue}`),
        })
        if (label) {
          valueLabel = label
        }
        const page = graph.findOne(FOAF('homepage'), {
          subject: $rdf.blankNode(`${propValue}`),
        })
        return {
          label: valueLabel,
          uri: page || null,
          resolved: true,
        }
      })
      metadataGroups.push({ fields: [field(value, data)] })
    }
  })

  const metacatPropertiesMap = new Map<string, string>()
  metacatPropertiesMap.set('hasStudyType', 'Study Type')
  metacatPropertiesMap.set('hasConsentType', 'Consent Type')
  metacatPropertiesMap.set('hasDataType', 'Data Type')
  metacatPropertiesMap.set('hasNumberOfPatients', 'Number of Patients')
  metacatPropertiesMap.set('hasRatioOfSexes', 'Ratio of Sexes (M:F)')
  metacatPropertiesMap.set('hasInclusionCriteria', 'Inclusion Criteria')
  metacatPropertiesMap.set('hasExclusionCriteria', 'Exclusion Criteria')
  metacatPropertiesMap.set('hasAgeRange', 'Age Range')
  metacatPropertiesMap.set('hasAdditionalData', 'Additional Data')
  metacatPropertiesMap.set('hasDataUseRestriction', 'Data Use Restriction')

  const isNumeric = (string) => Number.isFinite(+string)
  Array.from(metacatPropertiesMap.keys()).forEach((key) => {
    const value = metacatPropertiesMap.get(key)
    if (multivaluedProperties.has(key)) {
      const propertyValues = graph.findAll(METACAT(key))
      if (propertyValues.length > 0) {
        const data = propertyValues.map((propValue) => {
          let valueLabel = propValue
          if (!literalProperties.has(propValue)) {
            const label = graph.findOne(RDFS('label'), {
              subject: $rdf.blankNode(`${propValue}`),
            })
            if (label) {
              valueLabel = label
            }
          }
          if (isNumeric(valueLabel)) {
            valueLabel = Number(valueLabel).toLocaleString()
          }
          return {
            label: valueLabel,
            url: null,
            resolved: true,
          }
        })
        metadataGroups.push({ fields: [field(value, data)] })
      }
    } else {
      let propertyValue = graph.findOne(METACAT(key))
      if (isNumeric(propertyValue)) {
        propertyValue = Number(propertyValue).toLocaleString()
      }
      if (propertyValue) {
        const label = graph.findOne(RDFS('label'), {
          subject: $rdf.blankNode(`${propertyValue}`),
        })
        const data = {
          label: label || propertyValue,
          url: null,
          resolved: false,
        }
        metadataGroups.push({ fields: [field(value, data)] })
      }
    }
  })

  const dcatapPropertiesMap = new Map<string, string>()
  dcatapPropertiesMap.set('applicableLegislation', 'Applicable Legislation')
  Array.from(dcatapPropertiesMap.keys()).forEach((key) => {
    const value = dcatapPropertiesMap.get(key)
    if (multivaluedProperties.has(key)) {
      const propertyValues = graph.findAll(DCATAP(key))
      if (propertyValues.length > 0) {
        const data = propertyValues.map((propValue) => {
          const uri = propValue
          let valueLabel = propValue
          if (propValue.startsWith('http')) {
            const label = graph.findOne(RDFS('label'), {
              subject: $rdf.namedNode(`${propValue}`),
            })
            if (label) {
              valueLabel = label
            }
          }
          return {
            label: valueLabel,
            uri: uri.startsWith('http') ? uri : null,
            resolved: true,
          }
        })
        metadataGroups.push({ fields: [field(value, data)] })
      }
    } else {
      const propertyValue = graph.findOne(DCATAP(key))
      if (propertyValue) {
        const label = graph.findOne(RDFS('label'), {
          subject: $rdf.namedNode(`${propertyValue}`),
        })
        const data = {
          label: label || propertyValue,
          url: null,
          resolved: false,
        }
        metadataGroups.push({ fields: [field(value, data)] })
      }
    }
  })

  const hdcatapPropertiesMap = new Map<string, string>()
  hdcatapPropertiesMap.set('healthCategory', 'Health Category')
  hdcatapPropertiesMap.set('hasCodingSystem', 'Coding System')
  hdcatapPropertiesMap.set('numberOfUniqueIndividuals', 'Number of Unique Individuals')
  hdcatapPropertiesMap.set('minTypicalAge', 'Minimum Typical Age')
  hdcatapPropertiesMap.set('maxTypicalAge', 'Maximum Typical Age')
  hdcatapPropertiesMap.set('numberOfRecords', 'Number of Records')

  Array.from(hdcatapPropertiesMap.keys()).forEach((key) => {
    const value = hdcatapPropertiesMap.get(key)
    if (multivaluedProperties.has(key)) {
      const propertyValues = graph.findAll(HDCATAP(key))
      if (propertyValues.length > 0) {
        const data = propertyValues.map((propValue) => {
          const uri = propValue
          let valueLabel = propValue
          if (propValue.startsWith('http')) {
            const label = graph.findOne(RDFS('label'), {
              subject: $rdf.namedNode(`${propValue}`),
            })
            if (label) {
              valueLabel = label
            }
          }
          return {
            label: valueLabel,
            uri: uri.startsWith('http') ? uri : null,
            resolved: true,
          }
        })
        metadataGroups.push({ fields: [field(value, data)] })
      }
    } else {
      const propertyValue = graph.findOne(HDCATAP(key))
      if (propertyValue) {
        const label = graph.findOne(RDFS('label'), {
          subject: $rdf.blankNode(`${propertyValue}`),
        })
        const isNumericField = ['numberOfUniqueIndividuals', 'numberOfRecords'].includes(key)
        const displayValue = isNumericField && !Number.isNaN(Number(propertyValue))
          ? Number(propertyValue).toLocaleString()
          : propertyValue
        const data = {
          label: label || displayValue,
          url: null,
          resolved: false,
        }
        metadataGroups.push({ fields: [field(value, data)] })
      }
    }
  })

  const hdab = graph.findAll(HDCATAP('hdab'))
  if (hdab.length > 0) {
    const data = hdab.map((uri) => {
      const name = graph.findOne(FOAF('name'), {
        subject: $rdf.blankNode(`${uri}`),
      })
      const email = graph.findOne(FOAF('mbox'), {
        subject: $rdf.blankNode(`${uri}`),
      })
      let label = `${name}`
      if (email !== undefined) {
        label = `${name} (${email})`
      }
      return {
        label,
        uri: null,
        resolved: true,
      }
    })
    metadataGroups.push({ fields: [field('Data Access Body', data)] })
  }

  // const voidDataset = graph.findOne(METACAT('hasVoidDataset'))
  // if (voidDataset) {
  //   const totalTriples = graph.findOne(VOID('triples'), {
  //     subject: $rdf.namedNode(voidDataset),
  //   })
  //   metadataGroups.push({ fields: [field('Total Triples', totalTriples)] })
  // }

  return metadataGroups
}

function datasetMetadata(graph: Graph) {
  const isNumeric = (string) => Number.isFinite(+string)
  let classDataList = []
  let propertyDataList = []
  const types = graph.findAll(RDF('type'))
  const typeCheck = types.map((type) => {
    if (type.includes('void#Dataset')) {
      return true
    }
    return false
  })
  if (typeCheck.some((val) => val === true)) {
    const cPartitions = graph.findAll(VOID('classPartition'))
    if (cPartitions.length > 0) {
      classDataList = cPartitions.map((uri) => {
        const cls = graph.findOne(VOID('class'), {
          subject: $rdf.blankNode(`${uri}`),
        })
        const curie = compactIRI(cls)
        let count = graph.findOne(VOID('entities'), {
          subject: $rdf.blankNode(`${uri}`),
        })
        if (isNumeric(count)) {
          count = Number(count)
        }
        return {
          concept: cls,
          curie,
          count,
        }
      })
    }
    const pPartitions = graph.findAll(VOID('propertyPartition'))
    if (pPartitions.length > 0) {
      propertyDataList = pPartitions.map((uri) => {
        const property = graph.findOne(VOID('property'), {
          subject: $rdf.blankNode(`${uri}`),
        })
        const curie = compactIRI(property)
        let triples = graph.findOne(VOID('triples'), {
          subject: $rdf.blankNode(`${uri}`),
        })
        if (isNumeric(triples)) {
          triples = Number(triples)
        }
        return {
          property,
          curie,
          triples,
        }
      })
    }
  }
  const data = {
    classes: classDataList,
    properties: propertyDataList,
  }
  return data
}

function conceptAvailabilityMetadata(graph: Graph) {
  const isNumeric = (string) => Number.isFinite(+string)
  let dataList = []
  const types = graph.findAll(RDF('type'))
  const typeCheck = types.map((type) => {
    if (type.includes('ConceptAvailability')) {
      return true
    }
    return false
  })
  if (typeCheck.some((val) => val === true)) {
    const partitions = graph.findAll(VOID('classPartition'))
    if (partitions.length > 0) {
      dataList = partitions.map((uri) => {
        const cls = graph.findOne(VOID('class'), {
          subject: $rdf.blankNode(`${uri}`),
        })
        const curie = compactIRI(cls)
        let count = graph.findOne(VOID('entities'), {
          subject: $rdf.blankNode(`${uri}`),
        })
        if (isNumeric(count)) {
          count = Number(count)
        }
        let avgCount = graph.findOne(METACAT('hasAverageCount'), {
          subject: $rdf.blankNode(`${uri}`),
        })
        if (isNumeric(avgCount)) {
          avgCount = Number(avgCount).toFixed(2)
        }
        let minCount = graph.findOne(METACAT('hasMinCount'), {
          subject: $rdf.blankNode(`${uri}`),
        })
        if (isNumeric(minCount)) {
          minCount = Number(minCount)
        }
        let maxCount = graph.findOne(METACAT('hasMaxCount'), {
          subject: $rdf.blankNode(`${uri}`),
        })
        if (isNumeric(maxCount)) {
          maxCount = Number(maxCount)
        }
        let patientCount = graph.findOne(METACAT('hasPatientCount'), {
          subject: $rdf.blankNode(`${uri}`),
        })
        if (isNumeric(patientCount)) {
          patientCount = Number(patientCount)
        }
        const source = graph.findAll(METACAT('hasHospital'), {
          subject: $rdf.blankNode(`${uri}`),
        })
        source.sort((s1, s2) => s1.toLowerCase().localeCompare(s2.toLowerCase()))
        let codeFrequencyDataList = []
        const codeFrequency = graph.findOne(METACAT('hasCodeFrequency'), {
          subject: $rdf.blankNode(`${uri}`),
        })
        if (codeFrequency !== null) {
          const codeFrequencyPartitions = graph.findAll(VOID('classPartition'), {
            subject: $rdf.blankNode(`${codeFrequency}`),
          })
          if (codeFrequencyPartitions.length > 0) {
            codeFrequencyDataList = codeFrequencyPartitions.map((cfUri) => {
              const codeCls = graph.findOne(VOID('class'), {
                subject: $rdf.blankNode(`${cfUri}`),
              })
              const codeCurie = compactIRI(codeCls)
              const label = graph.findOne(RDFS('label'), {
                subject: $rdf.blankNode(`${cfUri}`),
              })
              let codeCount = graph.findOne(VOID('entities'), {
                subject: $rdf.blankNode(`${cfUri}`),
              })
              const comment = graph.findOne(RDFS('comment'), {
                subject: $rdf.blankNode(`${cfUri}`),
              })
              if (comment !== undefined) {
                codeCount = '< 10'
              }
              if (isNumeric(codeCount)) {
                codeCount = Number(codeCount)
              }
              const cfSource = graph.findAll(METACAT('hasHospital'), {
                subject: $rdf.blankNode(`${cfUri}`),
              })
              cfSource.sort((s1, s2) => s1.toLowerCase().localeCompare(s2.toLowerCase()))
              return {
                code: codeCls,
                curie: codeCurie,
                label,
                count: codeCount,
                hospital: cfSource.join(', '),
              }
            })
          }
        }
        return {
          concept: cls,
          curie,
          count,
          patientCount,
          codeFrequency: codeFrequencyDataList,
          avgCount,
          minCount,
          maxCount,
          hospital: source.length ? source.join(', ') : undefined,
        }
      })
    }
  }
  return dataList
}

function codeFrequencyMetadata(graph: Graph) {
  const isNumeric = (string) => Number.isFinite(+string)
  let dataList = []
  const types = graph.findAll(RDF('type'))
  const typeCheck = types.map((type) => {
    if (type.includes('CodeFrequency')) {
      return true
    }
    return false
  })
  if (typeCheck.some((val) => val === true)) {
    const partitions = graph.findAll(VOID('classPartition'))
    if (partitions.length > 0) {
      dataList = partitions.map((uri) => {
        const standard = graph.findOne(METACAT('hasStandard'), {
          subject: $rdf.blankNode(`${uri}`),
        })
        const cls = graph.findOne(VOID('class'), {
          subject: $rdf.blankNode(`${uri}`),
        })
        const label = graph.findOne(RDFS('label'), {
          subject: $rdf.blankNode(`${uri}`),
        })
        let count = graph.findOne(VOID('entities'), {
          subject: $rdf.blankNode(`${uri}`),
        })
        const comment = graph.findOne(RDFS('comment'), {
          subject: $rdf.blankNode(`${uri}`),
        })
        if (comment !== undefined) {
          count = '< 10'
        }
        if (isNumeric(count)) {
          count = Number(count)
        }
        let distinctCount = graph.findOne(METACAT('hasDistinctEntities'), {
          subject: $rdf.blankNode(`${uri}`),
        })
        if (isNumeric(distinctCount)) {
          distinctCount = Number(distinctCount)
        }
        let patientCount = graph.findOne(METACAT('hasPatientCount'), {
          subject: $rdf.blankNode(`${uri}`),
        })
        if (comment !== undefined) {
          patientCount = '< 10'
        }
        if (isNumeric(patientCount)) {
          patientCount = Number(patientCount)
        }
        const source = graph.findAll(METACAT('hasHospital'), {
          subject: $rdf.blankNode(`${uri}`),
        })
        source.sort((s1, s2) => s1.toLowerCase().localeCompare(s2.toLowerCase()))
        const curie = compactIRI(cls)
        const nonResolvable = ['ICD-10-GM', 'CHOP', 'ICD-O-3']
        let code = cls
        let resolvable = true
        if (nonResolvable.includes(standard as string)) {
          resolvable = false
          code = curie
        }
        return {
          standard,
          code: cls,
          curie,
          label,
          count,
          patientCount,
          distinctCount,
          hospital: source.join(', '),
          resolvable,
        }
      })
    }
  }
  return dataList
}

function compactIRI(iri) {
  const prefixMap = {
    'https://www.biomedit.ch/rdf/sphn-schema/sphn#': 'sphn',
    'https://biomedit.ch/rdf/sphn-schema/sphn/individual#': 'sphn-individual',
    'https://biomedit.ch/rdf/sphn-schema/sphn#': 'sphn',
    'https://biomedit.ch/rdf/sphn-schema/spo#': 'spo',
    'https://biomedit.ch/rdf/sphn-schema/luicd#': 'lucid',
    'https://biomedit.ch/rdf/sphn-schema/sph#': 'sph',
    'https://biomedit.ch/rdf/sphn-schema/iicu#': 'iicu',
    'https://biomedit.ch/rdf/sphn-schema/adonis#': 'adonis',
    'https://www.whocc.no/atc_ddd_index/': 'atc',
    'https://biomedit.ch/rdf/sphn-resource/chop/': 'chop',
    'https://biomedit.ch/rdf/sphn-resource/icd-10-gm/': 'icd-10-gm',
    'https://data.jrc.ec.europa.eu/collection/ICDO3_O#': 'icd-o-3',
    'http://purl.obolibrary.org/obo/GENEPIO_': 'genepio',
    'http://purl.obolibrary.org/obo/OBI_': 'obi',
    'http://purl.obolibrary.org/obo/HP_': 'hp',
    'https://loinc.org/rdf/': 'loinc',
    'https://biomedit.ch/rdf/sphn-resource/ucum/': 'ucum',
    'http://snomed.info/id/': 'snomed',
    'http://www.orpha.net/ORDO/Orphanet_': 'ordo',
  }
  const match = Object.keys(prefixMap).find((base) => iri.startsWith(base))
  if (!match) return iri
  const prefix = prefixMap[match]
  let reference = iri.slice(match.length)
  const queryMatch = reference.match(/[?&]code=([^&]+)/i)
  if (queryMatch) {
    return `${prefix}:${queryMatch[1]}`
  }
  reference = reference.replace(/^\/+/, '')
  return `${prefix}:${reference}`
}

function wrapShaclValue(fieldConfig, value) {
  if (!value) {
    return null
  }

  switch (fieldConfig.viewer) {
    case DASH('LabelViewer').value:
      return itemFromPath(value)
    case DASH('URIViewer').value:
      return { label: value, uri: value }
    default:
      if (fieldUtils.isDatetime(fieldConfig)) {
        return { label: moment(value).format(config.dateFormat) }
      }
      if (fieldConfig.datatype === XSD('boolean').value) {
        if (valueUtils.isTrue(value)) return { label: 'TRUE' }
        if (valueUtils.isFalse(value)) return { label: 'FALSE' }
      }
      return { label: value }
  }
}

function getShaclValue(graph: Graph, fieldConfig) {
  if (fieldConfig.maxCount === 1) {
    const value = graph.findOne($rdf.namedNode(fieldConfig.path))
    return wrapShaclValue(fieldConfig, value)
  }

  const values = graph.findAll($rdf.namedNode(fieldConfig.path))
  return values.map((v) => wrapShaclValue(fieldConfig, v)).filter((v) => v !== null)
}

function fromShaclField(graph: Graph, fieldConfig) {
  const name = fieldUtils.getName(fieldConfig)
  const value = getShaclValue(graph, fieldConfig)

  if (!value || _.isEmpty(value)) {
    return null
  }

  return field(name, getShaclValue(graph, fieldConfig))
}

export default {
  field,
  dateField,
  commonMetadata,
  datasetMetadata,
  conceptAvailabilityMetadata,
  codeFrequencyMetadata,
  itemFromPath,
  fromShaclField,
}
